import React, { useState, useEffect } from 'react';
import { Search, Plus, Calendar, Clock, AlertCircle, RefreshCw, Gift } from 'lucide-react';
import Button from '../ui/Button';
import Card from '../ui/Card';
import Badge from '../ui/Badge';
import LoadMoreButton from '../ui/LoadMoreButton';
import { purchaseService } from '../../services';
import { formatHours } from '../../utils/helpers';
import { PermissionGuard } from '../auth/PermissionGuard';
import { ButtonPermissions } from '../../types/permission';

const FinanceView = ({ onFinanceClick, onSearch, onAddFinance, searchParams = {}, searchTrigger = 0 }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const sentinelRef = React.useRef(null); // 滚动加载哨兵

  // 加载购课记录
  const loadRecords = async (pageNum = 1, append = false) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await purchaseService.getList({
        page: pageNum,
        per_page: 20,
        ...searchParams, // 合并搜索参数
      });

      const items = response.items || [];
      
      if (append) {
        setRecords(prev => [...prev, ...items]);
      } else {
        setRecords(items);
      }
      
      setHasMore(response.has_next || false);
      setPage(pageNum);
    } catch (err) {
      console.error('加载账务记录失败:', err);
      setError(err.message || '加载失败');
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  };

  // 初始加载
  useEffect(() => {
    loadRecords();
  }, []);

  // 搜索触发时重新加载（包括选择“全部”）
  useEffect(() => {
    if (searchTrigger > 0) {
      loadRecords(1);
    }
  }, [searchTrigger]);

  // 加载更多
  const handleLoadMore = () => {
    if (!loading && !isLoadingMore && hasMore) {
      setIsLoadingMore(true);
      loadRecords(page + 1, true);
    }
  };

  // 使用 IntersectionObserver 监听滚动到底部（与首页一致）
  useEffect(() => {
    if (!sentinelRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && !isLoadingMore && !loading && hasMore && !error) {
          handleLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(sentinelRef.current);

    return () => {
      if (sentinelRef.current) {
        observer.unobserve(sentinelRef.current);
      }
    };
  }, [hasMore, isLoadingMore, loading, page]);

  return (
    <div className="flex flex-col h-full bg-[#F7FAFC]">
      {/* Header */}
      <header className="h-[48px] px-4 bg-white flex items-center justify-between border-b border-[#EDF2F7] flex-shrink-0">
        <h1 className="text-base font-bold text-[#2D3748]">账务明细</h1>
        <div className="flex gap-2">
          <Button 
            variant="icon" 
            icon={RefreshCw} 
            onClick={() => loadRecords(1)} 
            disabled={loading}
            className={loading ? 'animate-spin' : ''}
          />
          <Button variant="icon" icon={Search} onClick={() => onSearch('finance')} />
          <PermissionGuard permission={ButtonPermissions.purchase.create}>
            <Button variant="primary" size="small" icon={Plus} onClick={onAddFinance}>
              新增
            </Button>
          </PermissionGuard>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 overflow-y-auto px-4 pt-4 pb-24 space-y-3">
        {/* 错误提示 */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-red-700 font-medium">加载失败</p>
              <p className="text-xs text-red-600 mt-1">{error}</p>
              <button 
                onClick={() => loadRecords(1)}
                className="mt-2 text-xs text-red-700 underline"
              >
                重试
              </button>
            </div>
          </div>
        )}

        {/* 加载中 */}
        {loading && records.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12">
            <RefreshCw size={32} className="text-[#3182CE] animate-spin mb-3" />
            <p className="text-sm text-[#718096]">加载中...</p>
          </div>
        )}

        {/* 记录列表 - 表格风格 */}
        {records.length > 0 && (
          <div className="bg-white rounded-xl overflow-hidden border border-[#EDF2F7]">
            {/* 表头 */}
            <div className="grid grid-cols-[1fr_auto] items-center px-3 py-2 bg-[#F7FAFC] border-b border-[#EDF2F7]">
              <span className="text-[11px] font-semibold text-[#718096] uppercase tracking-wide">学员 / 课程</span>
              <span className="text-[11px] font-semibold text-[#718096] uppercase tracking-wide text-right">金额 / 日期</span>
            </div>
            {/* 表格行 */}
            {records.map((record, idx) => (
              <div
                key={record.id}
                onClick={() => onFinanceClick(record)}
                className={`grid grid-cols-[1fr_auto] items-center px-3 py-2.5 gap-2 active:bg-blue-50 transition-colors cursor-pointer ${
                  idx !== records.length - 1 ? 'border-b border-[#EDF2F7]' : ''
                } ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'}`}
              >
                {/* 左侧：学员 + 课程 + 状态 */}
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                    <span className="font-semibold text-[13px] text-[#2D3748] truncate max-w-[80px]">
                      {record.student_name || '未知'}
                    </span>
                    <span className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded font-medium truncate max-w-[90px]">
                      {record.course_name}
                    </span>
                    {/* 单据状态 */}
                    {record.status === 'voided' && (
                      <span className="text-[10px] bg-red-50 text-red-500 px-1.5 py-0.5 rounded font-medium flex-shrink-0">作废</span>
                    )}
                    {record.status === 'adjusted' && (
                      <span className="text-[10px] bg-orange-50 text-orange-500 px-1.5 py-0.5 rounded font-medium flex-shrink-0">调整</span>
                    )}
                    {record.status === 'active' && record.payment_method === 'VOID' && (
                      <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-medium flex-shrink-0">冲账</span>
                    )}
                    {record.status === 'active' && record.payment_method === 'ADJUSTMENT' && (
                      <span className="text-[10px] bg-purple-50 text-purple-500 px-1.5 py-0.5 rounded font-medium flex-shrink-0">调整单</span>
                    )}
                    {(record.has_referral || record.has_volume_bonus) && (
                      <span className="text-[10px] bg-purple-50 text-purple-600 px-1.5 py-0.5 rounded font-black border border-purple-100 flex-shrink-0 flex items-center gap-0.5">
                        <Gift size={9} />赠
                      </span>
                    )}
                  </div>
                  {/* 课时 + 支付方式 */}
                  <div className="flex items-center gap-2 text-[11px] text-[#718096]">
                    {record.total_hours && (
                      <span className="flex items-center gap-0.5">
                        <Clock size={9} />
                        {formatHours(record.total_hours)}
                      </span>
                    )}
                    <span className="text-[10px] bg-orange-50 text-orange-500 px-1.5 py-0.5 rounded font-medium flex-shrink-0">
                      {record.payment_method || '未知'}
                    </span>
                  </div>
                </div>

                {/* 右侧：金额 + 日期 */}
                <div className="flex flex-col items-end flex-shrink-0">
                  <span className={`text-base font-black leading-tight ${
                    record.status === 'voided' ? 'text-gray-400 line-through' : 'text-[#3182CE]'
                  }`}>
                    ¥{Math.round(record.amount_paid || 0)}
                  </span>
                  <span className="text-[10px] text-[#718096] flex items-center gap-0.5 mt-0.5">
                    <Calendar size={9} />
                    {record.date ? record.date.split(' ')[0] : '未知'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* 空状态 */}
        {!loading && !error && records.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12">
            <Calendar size={48} className="text-gray-300 mb-3" />
            <p className="text-sm text-[#718096]">暂无账务记录</p>
            <PermissionGuard permission={ButtonPermissions.purchase.create}>
              <button 
                onClick={onAddFinance}
                className="mt-3 text-xs text-[#3182CE] underline"
              >
                添加第一条记录
              </button>
            </PermissionGuard>
          </div>
        )}

        {/* 加载更多哨兵 */}
        <div ref={sentinelRef} className="h-4 w-full" />

        {/* 加载分页 */}
        <div className="pb-20">
          <LoadMoreButton 
            onClick={handleLoadMore} 
            loading={loading || isLoadingMore} 
            hasMore={hasMore && records.length > 0} 
          />
        </div>
      </main>
    </div>
  );
};

export default FinanceView;
